import { Parameter } from "domain/entities/parameter";
import { SupportTrip } from "domain/entities/support-trip";
import { DistanceTypeEnum } from "domain/enum/distanceType";
import { EMAIL } from "domain/enum/email";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { DistanceInputFilter } from "domain/models/distance-input-filter";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import InputDateComponent from "presentation/components/Shared/InputDateComponent";
import { useTripLocationsByFilter } from "presentation/hooks/use-query-distance";
import { useParameterByFilter } from "presentation/hooks/use-query-parameter";
import { useCreateSupportTripMutation, useDeleteSupportTripMutation, useUpdateSupportTripMutation } from "presentation/hooks/use-query-support-trip";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useFilterAdminSupportTripStore } from "presentation/stores/use-filter-admin-support-trip-store";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import { useSupportTripCancelStore } from "presentation/stores/use-support-trip-cancel-store";
import { useSupportTripDestinationStore } from "presentation/stores/use-support-trip-destination-store";
import { useSupportTripOriginStore } from "presentation/stores/use-support-trip-origin-store";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import "./index.css";
import SupportTripsGrid from "./SupportTripsGrid";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { Employee } from "domain/entities/employee";
import InputTextComponent from "presentation/components/Shared/InputTextComponent";
import InputNumberComponent from "presentation/components/Shared/InputNumberComponent";
import Spinner from "presentation/components/Shared/Spinner";


interface SupportTripTabProps {
  isAdmin: boolean;
  isFree: boolean;
}

interface SupportTripAdminFilterParams {
  dateToAnalyse?: Date;
  employeeToAnalyse: Employee;
  statusToAnalyse?: string;
}

const SupportTripTab: React.FC<SupportTripTabProps> = ({ isAdmin, isFree }) => {
  const toast = useRef<Toast>(null);
  const { userEmployee: userEmployee, email: userEmployeeEmail, userPermissions } = useAuthStore();

  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message: message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  const [createSupportTrip, setCreateSupportTrip] = useState<Partial<SupportTrip>>({
    locationOrigin: "",
    locationDestination: "",
    dateTrip: undefined,
    distanceKm: undefined
  });

  const [adminFilters, setAdminFilters] = useState<SupportTripAdminFilterParams>(
    {
      dateToAnalyse: undefined,
      employeeToAnalyse: {
        id: "todos",
        name: "TODOS",
      } as Employee,
      statusToAnalyse: undefined,
    }
  );

  const {filters: filterAdminSupportTrip, setFilters: setFilterAdminSupportTrip} = useFilterAdminSupportTripStore();

  const { supportTripOrigin: supportTripOrigin, setSupportTripOrigin } = useSupportTripOriginStore();
  const { supportTripDestination: supportTripDestination, setSupportTripDestination } = useSupportTripDestinationStore();

  const { data: tripsOriginData, isError: isErrorTripsOrigin, isLoading: isLoadingTripsOrigin, error: errorTripsOrigin, refetch: refetchExistentTripsOrigin } = useTripLocationsByFilter({
    type: DistanceTypeEnum.VIAGEM_SUPORTE,
    sortBy: "locationOrigin",
    sortOrder: "asc",
    page: 1,
    limit: 500
  } as DistanceInputFilter);

  const { data: tripsDestinationData, isError: isErrorTripsDestination, isLoading: isLoadingTripsDestination, error: errorTripsDestination, refetch: refetchExistentTripsDestination } = useTripLocationsByFilter({
    type: DistanceTypeEnum.VIAGEM_SUPORTE,
    locationOrigin: createSupportTrip.locationOrigin,
    sortBy: "locationDestination",
    sortOrder: "asc",
    page: 1,
    limit: 500
  } as DistanceInputFilter);

  const [employees, setEmployees] = useState<Parameter[]>();

  const [employeeToAnalyse, setEmployeeToAnalyse] = useState<Parameter>();

  const { data: employeesData, refetch: refetchEmployees } = useEmployeesByFilter({ 
    limit: 500,
    sortBy: "name"
  } as EmployeeInputFilter);

  useEffect(() => {
    const formatedEmployees = employeesData?.data?.map((item) => {
      const employee: Parameter = {
        id: item.id,
        value: item.name,
        type: "employee",
      };
      return employee;
    });

    const allEmployeesOption: Parameter = {
      id: "todos",
      value: "TODOS",
      type: "employee",
    };

    setEmployees([allEmployeesOption, ...(formatedEmployees ?? [])]);

  }, [employeesData]);

  const { data: parameters } = useParameterByFilter({
      limit: 1000,
      sortBy: "value",
    } as BaseInputFilter);

  useEffect(() => {
    setSupportTripStatus(
      parameters?.data?.filter((item) => item.type === "supportTripStatus")
    );
  }, [parameters]);

  const { supportTripStatus, setSupportTripStatus } = useParameterStore();

  useEffect(() => {
    if (tripsOriginData?.data) {
      const tripOriginsMap = new Map<string, Parameter>();

      tripsOriginData.data.forEach((item) => {
        const key = item.locationOrigin?.trim();
        if (key && !tripOriginsMap.has(key)) {
          tripOriginsMap.set(key, {
            value: item.locationOrigin,
            id: item.locationOrigin,
            type: "tripOrigin",
            createdAt: item.createdAt,
            modifiedAt: item.modifiedAt,
          });
        }
      });
      const uniqueTripOrigins = Array.from(tripOriginsMap.values());
      setSupportTripOrigin(uniqueTripOrigins);
    }
  }, [tripsOriginData]);

  useEffect(() => {
    if (tripsDestinationData?.data) {
      const tripDestinationsMap = new Map<string, Parameter>();

      tripsDestinationData.data.forEach((item) => {
        const key = item.locationDestination?.trim();
        if (key && !tripDestinationsMap.has(key)) {
          tripDestinationsMap.set(key, {
            value: item.locationDestination,
            id: item.locationDestination,
            type: "tripDestination",
            createdAt: item.createdAt,
            modifiedAt: item.modifiedAt,
          });
        }
      });
      const uniqueTripDestinations = Array.from(tripDestinationsMap.values());
      setSupportTripDestination(uniqueTripDestinations);
    }
  }, [tripsDestinationData]);

  const { supportTripToCancel, setSupportTripToCancel } = useSupportTripCancelStore();

  const {
    mutate: createSupportTripMutation,
    error: creationSupportTripError,
    isError: isCreationSupportTripError,
    isSuccess: isCreationSupportTripSuccess,
    isPending: isCreationPending,
    data: supportTripCreated,
  } = useCreateSupportTripMutation();

  const handleCreate = async () => {
    if (createSupportTrip) {

      if (createSupportTrip.distanceKm && createSupportTrip.distanceKm <= 0)
        toast.current?.show({
          severity: "warn",
          summary: "Aviso",
          detail: "A distância da viagem (em km) tem de ser superior a 0.",
          life: 3000,
        });
      else {
        createSupportTrip.employee = {
          ...createSupportTrip.employee,
          id: userEmployee?.id ?? "",
          name: userEmployee?.name ?? ""
        };
        createSupportTrip.lastModifiedBy = userEmployeeEmail || EMAIL;
        try {
          createSupportTripMutation(createSupportTrip);
        } catch (e) {
          console.error(e);
        }
      }
    }
  };


  const { mutate: updateSupportTrip, error: updatingSupportTripError, isSuccess: isSuccessUpdateSupportTrip, isError: isErrorUpdateSupportTrip, isPending: isPendingUpdateSupportTrip } =
      useUpdateSupportTripMutation();

  useEffect(() => {
    if (!isFree) {
      createSupportTrip.locationDestination = "";
      setCreateSupportTrip(createSupportTrip);
      refetchExistentTripsDestination();
    }
  }, [createSupportTrip?.locationOrigin]);

  useEffect(() => {
    if (adminFilters?.dateToAnalyse) {
      let dateDateToAnalyse = new Date(adminFilters.dateToAnalyse);
      dateDateToAnalyse = new Date(dateDateToAnalyse.setHours(12, 0, 0, 0));
      filterAdminSupportTrip.month = dateDateToAnalyse.getMonth() + 1;
      filterAdminSupportTrip.year = dateDateToAnalyse.getFullYear();
      setFilterAdminSupportTrip(filterAdminSupportTrip);
    }
    if (adminFilters?.statusToAnalyse) {
      filterAdminSupportTrip.status = adminFilters.statusToAnalyse;
      setFilterAdminSupportTrip(filterAdminSupportTrip);
    }
    if (!adminFilters?.dateToAnalyse) {
      filterAdminSupportTrip.month = undefined;
      filterAdminSupportTrip.year = undefined;
      setFilterAdminSupportTrip(filterAdminSupportTrip);
    }
    if (!adminFilters?.statusToAnalyse) {
      filterAdminSupportTrip.status = undefined;
      setFilterAdminSupportTrip(filterAdminSupportTrip);
    }
  }, [adminFilters?.dateToAnalyse, adminFilters?.statusToAnalyse]);

  useEffect(() => {
    if (adminFilters?.employeeToAnalyse) {
      const empToAnalyse: Parameter = {
        id: adminFilters.employeeToAnalyse.id,
        value: adminFilters.employeeToAnalyse.name,
        type: "employee",
      };

      setEmployeeToAnalyse(empToAnalyse);
    }
  }, [adminFilters?.employeeToAnalyse]);

  useEffect(() => {

    if (employeeToAnalyse?.id) {
      if (employeeToAnalyse?.id.toLowerCase() === "todos")
        filterAdminSupportTrip.employeeId = undefined;
      else  
        filterAdminSupportTrip.employeeId = employeeToAnalyse?.id;
      setFilterAdminSupportTrip(filterAdminSupportTrip);
    }
    if (!employeeToAnalyse?.id) {
      console.log("ENTREI AQUI!!")
      filterAdminSupportTrip.employeeId = undefined;
      setFilterAdminSupportTrip(filterAdminSupportTrip);
    }

  }, [employeeToAnalyse?.id]);


  useEffect(() => {
    if (supportTripToCancel && supportTripToCancel.id !== "") {
      supportTripToCancel.lastModifiedBy = userEmployeeEmail || EMAIL;
      try {
        updateSupportTrip(supportTripToCancel);
      } catch (e) {
        console.error(e);
      }
    }
  }, [supportTripToCancel]);


  useEffect(() => {
    if (isSuccessUpdateSupportTrip) {
      const successMessage = "Viagem atualizada com sucesso";

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: successMessage,
        life: 2000,
      });

      setSupportTripToCancel({ locationOrigin: "", locationDestination: "", dateTrip: undefined, id: "", distanceKm: undefined });

    }
  }, [isSuccessUpdateSupportTrip]);

  useEffect(() => {
    if (isErrorUpdateSupportTrip && updatingSupportTripError) {
      let messages: string[] = [];
      const error: any = updatingSupportTripError;
      if (error.errors && Array.isArray(error.errors)) {
        messages = error.errors;
      } else if (error.response?.data?.metadata?.errors) {
        messages = error.response.data.metadata.errors;
      } else {
        messages.push(error.message);
      }
      console.log("message", messages);
      messages.forEach((item: string) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 2000,
        });
      });
    }
  }, [updatingSupportTripError, isErrorUpdateSupportTrip]);

  useEffect(() => {
    if (isCreationSupportTripSuccess) {
      const successMessage = "Viagem criada com sucesso";

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: successMessage,
        life: 2000,
      });

      setCreateSupportTrip({ locationOrigin: "", locationDestination: "", dateTrip: undefined, distanceKm: undefined });

    }
  }, [isCreationSupportTripSuccess]);

  useEffect(() => {
    if (isCreationSupportTripError && creationSupportTripError) {
      let messages: string[] = [];
      const error: any = creationSupportTripError;
      if (error.errors && Array.isArray(error.errors)) {
        messages = error.errors;
      } else if (error.response?.data?.metadata?.errors) {
        messages = error.response.data.metadata.errors;
      } else {
        messages.push(error.message);
      }
      console.log("message", messages);
      messages.forEach((item: string) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 2000,
        });
      });
    }
  }, [creationSupportTripError, isCreationSupportTripError]);

  if (isCreationPending) {
    return <Spinner />;
  }
  if (isPendingUpdateSupportTrip) {
    return <Spinner />;
  }


  return (
    <div className="parameter-grid ">

      {!isAdmin && (
        <>
          <div className="parameter-1-col">
            <InputDateComponent
              label="Data"
              fieldName="dateTrip"
              currentObject={createSupportTrip}
              getValue={createSupportTrip?.dateTrip}
              setValue={(value) => setCreateSupportTrip(value)}
            />
          </div>

          {!isFree && (
            <>
              <div className="parameter-1-col">
                <DropdownComponent
                  options={supportTripOrigin}
                  label={`Origem`}
                  fieldName="locationOrigin"
                  currentObject={createSupportTrip}
                  getValue={createSupportTrip?.locationOrigin ?? ""}
                  setValue={(value) => setCreateSupportTrip(value)}
                />
              </div>
              <div className="parameter-1-col">
                <DropdownComponent
                  options={supportTripDestination}
                  label={`Destino`}
                  fieldName="locationDestination"
                  currentObject={createSupportTrip}
                  getValue={createSupportTrip?.locationDestination ?? ""}
                  setValue={(value) => setCreateSupportTrip(value)}
                  isDisable={!createSupportTrip?.locationOrigin}
                />
              </div>
            </>
          )}

          {isFree && (
            <>
              <div className="parameter-1-col">
                <InputTextComponent
                  label={`Origem`}
                  fieldName="locationOrigin"
                  currentObject={createSupportTrip}
                  getValue={createSupportTrip?.locationOrigin ?? ""}
                  setValue={(value) => setCreateSupportTrip(value)}
                />
              </div>
              <div className="parameter-1-col">
                <InputTextComponent
                  label={`Destino`}
                  fieldName="locationDestination"
                  currentObject={createSupportTrip}
                  getValue={createSupportTrip?.locationDestination ?? ""}
                  setValue={(value) => setCreateSupportTrip(value)}
                />
              </div>
              <div className="parameter-1-col">
                <InputNumberComponent
                  label={`Distância (km)`}
                  fieldName="distanceKm"
                  currentObject={createSupportTrip}
                  getValue={createSupportTrip?.distanceKm}
                  setValue={(value) => setCreateSupportTrip(value)}
                  allowDecimals={true}
                />
              </div>
            </>
          )}


          <div className="parameter-1-col combine-buttons">
            <>
              <Button
                label={"Adicionar"}
                onClick={() =>
                  confirmAction("Deseja registar esta nova viagem?", handleCreate)
                }
                className="button-size "
                disabled={
                  isFree ?
                    !createSupportTrip ||
                    !createSupportTrip.locationOrigin ||
                    !createSupportTrip.locationDestination ||
                    !createSupportTrip.dateTrip ||
                    !createSupportTrip.distanceKm
                    :
                    !createSupportTrip ||
                    !createSupportTrip.locationOrigin ||
                    !createSupportTrip.locationDestination ||
                    !createSupportTrip.dateTrip
                }
              />
            </>
          </div>
        </>
      )}

      {isAdmin && (
        <>
          <div className="parameter-1-col">
            <DropdownComponent
              options={employees ?? []}
              label={`Colaborador`}
              isValueId={true}
              fieldName="employeeToAnalyse"
              currentObject={adminFilters}
              getValue={adminFilters?.employeeToAnalyse?.id ?? ""}
              setValue={(value) => setAdminFilters(value)}
            />
          </div>

          <div className="parameter-1-col">
            <InputDateComponent
              label="Data"
              fieldName="dateToAnalyse"
              currentObject={adminFilters}
              getValue={adminFilters?.dateToAnalyse}
              setValue={(value) => setAdminFilters(value)}
              view="month"
              dateFormat="mm/yy"
            />
          </div>

          <div className="parameter-1-col">
            <DropdownComponent
              options={supportTripStatus}
              label={`Status`}
              fieldName="statusToAnalyse"
              currentObject={adminFilters}
              getValue={adminFilters?.statusToAnalyse}
              setValue={(value) => setAdminFilters(value)}
              showClear={true}
            />
          </div>
        </>
      )}

      <div className="timesheet-4-cols">
        <SupportTripsGrid
          isAdmin={isAdmin}
        />
      </div>

      <Toast ref={toast} position="bottom-right" />

      <ConfirmDialog />
    </div>
  );
};
export default SupportTripTab;
